import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../api/client.js';

export interface EngagementSummary {
  id: number;
  client_id: number;
  name: string;
  code: string;
  current_phase: string;
  client_name?: string;
}

interface EngagementCtx {
  engagements: EngagementSummary[];
  current: EngagementSummary | null;
  setCurrentId: (id: number | null) => void;
  reload: () => Promise<void>;
  /**
   * Resolves once the engagement list has arrived (or failed) — never re-fetching if it is
   * already in flight or in hand.
   *
   * This exists for one reason (FEASIBILITY G-9): `current` is derived by finding `currentId`
   * inside `engagements`, and `engagements` is fetched asynchronously on mount. `openRoute`
   * sets `currentId` and then the hash, so on a COLD page the destination used to mount with
   * `current === null` until that fetch resolved — a page that says "no engagement selected"
   * for a beat after a click that named one. Awaiting this before navigating closes it.
   */
  ensureLoaded: () => Promise<EngagementSummary[]>;
}

const Ctx = createContext<EngagementCtx>({
  engagements: [],
  current: null,
  setCurrentId: () => {},
  reload: async () => {},
  ensureLoaded: async () => [],
});

export function EngagementProvider({ children }: { children: ReactNode }) {
  const [engagements, setEngagements] = useState<EngagementSummary[]>([]);
  const [currentId, setCurrentIdState] = useState<number | null>(() => {
    const stored = localStorage.getItem('signet.engagement');
    return stored ? Number(stored) : null;
  });
  // The in-flight (or settled) list fetch. A ref, not state: waiting on it must not re-render.
  const inFlight = useRef<Promise<EngagementSummary[]> | null>(null);
  // `reload`'s stale-selection check needs the CURRENT id without making the fetch depend on it.
  const currentIdRef = useRef<number | null>(currentId);
  currentIdRef.current = currentId;

  const fetchList = useCallback(async (): Promise<EngagementSummary[]> => {
    try {
      const list = await api.getEngagements();
      setEngagements(list);
      // A selection that no longer exists is not a selection.
      if (currentIdRef.current && !list.some((e: EngagementSummary) => e.id === currentIdRef.current)) {
        setCurrentIdState(null);
      }
      return list;
    } catch {
      // not logged in yet / server starting — page components handle their own errors
      return [];
    }
  }, []);

  const ensureLoaded = useCallback((): Promise<EngagementSummary[]> => {
    if (!inFlight.current) inFlight.current = fetchList();
    return inFlight.current;
  }, [fetchList]);

  const reload = useCallback(async (): Promise<void> => {
    inFlight.current = fetchList();
    await inFlight.current;
  }, [fetchList]);

  useEffect(() => { void ensureLoaded(); }, [ensureLoaded]);

  const setCurrentId = useCallback((id: number | null) => {
    setCurrentIdState(id);
    if (id) localStorage.setItem('signet.engagement', String(id));
    else localStorage.removeItem('signet.engagement');
  }, []);

  const current = engagements.find((e) => e.id === currentId) ?? null;

  return (
    <Ctx.Provider value={{ engagements, current, setCurrentId, reload, ensureLoaded }}>
      {children}
    </Ctx.Provider>
  );
}

export const useEngagement = () => useContext(Ctx);
